// @givmo/app-charity — pending removal-request panel (CPA-WEB7).
//
// Shown INSTEAD of the request button when an OPEN removal request already exists (the backend is
// idempotent — a second request 409s, so we never offer the button while one is in flight). It surfaces:
//   - the plain-language status (REQUESTED → "Identity check pending"; IDENTITY_VERIFIED → "Processing
//     removal") as a toned Badge — never internal grant vocabulary;
//   - the 3+3-business-day SLA: the requested date + the CURRENT stage's deadline (identity-check-by while
//     REQUESTED, removal-by while IDENTITY_VERIFIED);
//   - a reassurance that grant payouts continue (the D-CP-11 rail persists here too — a charity mid-removal
//     must not fear its money stopped).

import type { CharityRemovalRequestOut } from '@givmo/api-client';
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Icon,
  testIdProp,
  type LucideLikeIcon,
} from '@givmo/design-system';
import { BadgeCheck, Clock, LogOut } from 'lucide-react';
import {
  activeRemovalDeadline,
  formatRemovalDate,
  removalStatusBadgeTone,
  removalStatusLabel,
  REMOVAL_SLA_COPY,
} from './removal-decode';
import { ExternalStatusRefresh } from '../../lib/external-status-refresh';

const PREFIX = 'settings-removal';

export interface RemovalPendingViewProps {
  request: CharityRemovalRequestOut;
  lastCheckedAt: number;
  refreshing: boolean;
  onRefresh: () => void;
}

export function RemovalPendingView({
  request,
  lastCheckedAt,
  refreshing,
  onRefresh,
}: RemovalPendingViewProps) {
  const { label: deadlineLabel, deadline } = activeRemovalDeadline(request);

  return (
    <Card testId={`${PREFIX}-pending`}>
      <CardHeader>
        <div className="flex w-full items-center justify-between gap-10">
          <div className="flex items-center gap-10">
            <span className="flex size-32 items-center justify-center rounded-full bg-warning-50 text-warning-600">
              <Icon icon={LogOut} size={18} aria-hidden />
            </span>
            <div>
              <div className="font-w600 text-text-strong">Removal request in progress</div>
              <div className="text-s13 text-text-tertiary">
                We’re processing your request to remove this organization.
              </div>
            </div>
          </div>
          <Badge tone={removalStatusBadgeTone(request.status)} dot testId={`${PREFIX}-status`}>
            {removalStatusLabel(request.status)}
          </Badge>
        </div>
      </CardHeader>
      <CardBody className="flex flex-col gap-16">
        <ExternalStatusRefresh
          lastCheckedAt={lastCheckedAt}
          refreshing={refreshing}
          onRefresh={onRefresh}
          testId={`${PREFIX}-freshness`}
        />
        <dl className="grid grid-cols-1 gap-12 sm:grid-cols-2" {...testIdProp(`${PREFIX}-sla`)}>
          <Detail
            icon={Clock}
            term="Requested"
            value={formatRemovalDate(request.created_at)}
            testId={`${PREFIX}-requested-at`}
          />
          <Detail
            icon={Clock}
            term={deadlineLabel}
            value={formatRemovalDate(deadline)}
            testId={`${PREFIX}-deadline`}
          />
        </dl>

        <p className="text-s13 text-text-tertiary" {...testIdProp(`${PREFIX}-sla-copy`)}>
          Our team verifies your identity and then completes the removal, typically within{' '}
          {REMOVAL_SLA_COPY}. We’ll be in touch if we need anything from you.
        </p>

        {/* ★ The payout-continues reassurance (D-CP-11) — persists while the request is open. */}
        <div
          className="flex items-start gap-10 rounded-lg border border-border-subtle bg-surface-sunken p-14"
          {...testIdProp(`${PREFIX}-payout-note`)}
        >
          <Icon icon={BadgeCheck} size={18} className="mt-1 shrink-0 text-brand-600" aria-hidden />
          <p className="text-s13 text-text-secondary">
            Your grant payments are not affected while this is processed. Any grants your
            organization is owed still reach it, to your bank account if it’s approved for payments,
            otherwise by mailed check.
          </p>
        </div>
      </CardBody>
    </Card>
  );
}

function Detail({
  icon,
  term,
  value,
  testId,
}: {
  icon: LucideLikeIcon;
  term: string;
  value: string;
  testId: string;
}) {
  return (
    <div className="flex items-start gap-8">
      <Icon icon={icon} size={16} className="mt-[2px] shrink-0 text-icon-muted" aria-hidden />
      <div className="min-w-0">
        <dt className="text-s12 text-text-tertiary">{term}</dt>
        <dd className="text-s14 font-w500 text-text-strong" {...testIdProp(testId)}>
          {value}
        </dd>
      </div>
    </div>
  );
}
